/** Pure helpers for PlanPage DnD drop routing. */
import { resolveDndTaskId } from '@/features/tasks/utils/interactions/dragHandlers.js';
import { PLANNING_REGIMES } from '@/features/planning/config/planningConfig';
import { calculateNextAvailableTime } from '@/features/tasks/utils/views/taskTimeUtils';
import { isMainTask } from '@/features/tasks/components/atoms/taskMetadataViewState';
import { cascadeDateOrderUpdates } from '@/features/planning/utils/planRailReorderCompute';
import { applyCascadeDateOrderUpdates } from '@/features/planning/utils/planRailReorderApply';

const GRID_DROP_TYPES = new Set(['timeslot', 'month-day']);

/** Day-grid TimeSlot has no `type` — tagged `droppable: true` instead. */
export const isGridDropTarget = (overData) =>
  GRID_DROP_TYPES.has(overData?.type) || overData?.droppable === true;

export const resolveTimeSlot = (overData, allTasks, activeId) => {
  const { date, timeSlot } = overData;
  if (timeSlot.startsWith('regime-')) {
    const regimeIndex = parseInt(timeSlot.split('-')[1], 10);
    const regime = PLANNING_REGIMES[regimeIndex];
    if (regime) {
      return calculateNextAvailableTime(
        date,
        regime,
        allTasks.filter((t) => t.id !== activeId),
      );
    }
  } else if (timeSlot === 'allday') {
    return 'allday';
  }
  return null;
};

const applyTimeslotDrop = ({
  overData,
  taskId,
  allTasks,
  tasksMap,
  updateTask,
  userId,
}) => {
  const timeValue = resolveTimeSlot(overData, allTasks, taskId);
  if (!timeValue) return;
  const task = tasksMap?.get(String(taskId));
  const payload = { date: new Date(overData.date) };
  if (timeValue === 'allday' || !isMainTask(task, tasksMap)) {
    payload.time = timeValue;
  }
  updateTask(taskId, payload, userId);
};

const applyPlanRailReorder = ({
  over,
  taskId,
  planRailTasks,
  moveTask,
  updateTask,
  userId,
  tasksMap,
}) => {
  const overId = resolveDndTaskId(over);
  const updates = cascadeDateOrderUpdates(planRailTasks, taskId, overId);
  applyCascadeDateOrderUpdates(updates, {
    moveTask,
    updateTask,
    userId,
    tasksMap,
  });
};

/**
 * Routes a completed drag to timeslot / month-day / plan-rail / default handlers.
 */
export const applyPlanPageDragEnd = ({
  active,
  over,
  allTasks,
  tasksMap,
  userId,
  updateTask,
  moveTask,
  planMode,
  planRailTasks,
  routeDropHandler,
}) => {
  if (!over || !active || active.id === over.id) return;
  if (!userId) return;

  const taskId = resolveDndTaskId(active);
  const overData = over.data.current;

  if (overData?.type === 'timeslot') {
    applyTimeslotDrop({
      overData,
      taskId,
      allTasks,
      tasksMap,
      updateTask,
      userId,
    });
    return;
  }
  if (overData?.type === 'month-day') {
    updateTask(taskId, { date: new Date(overData.date) }, userId);
    return;
  }
  if (planMode && overData?.type === 'unscheduled-task') {
    applyPlanRailReorder({
      over,
      taskId,
      planRailTasks,
      moveTask,
      updateTask,
      userId,
      tasksMap,
    });
    return;
  }

  routeDropHandler(active, over);
};
